import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useApp } from "@/components/AppShell";
import { STATUSES, ISSUE_TYPES } from "@/lib/meta";
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";

export default function Reports() {
  const { projects } = useApp();
  const [projectId, setProjectId] = useState("all");
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    const params = projectId === "all" ? {} : { project_id: projectId };
    api.get("/issues", { params }).then(r => setIssues(r.data));
  }, [projectId]);

  const byStatus = STATUSES.map(s => ({ name: s.label, count: issues.filter(i => i.status === s.id).length }));
  const byType = ISSUE_TYPES.map(t => ({ name: t.label, count: issues.filter(i => i.issue_type === t.id).length }));
  const donePts = issues.filter(i => i.status === "done").reduce((a,b)=>a+(b.story_points||0), 0);
  const totalPts = issues.reduce((a,b)=>a+(b.story_points||0), 0);
  const COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#64748b"];

  // Simple burndown: bucket by created date
  const byDay = {};
  issues.forEach(i => {
    const d = (i.created_at || "").slice(0,10);
    byDay[d] = (byDay[d] || 0) + 1;
  });
  const trend = Object.entries(byDay).sort().slice(-14).map(([d, count]) => ({ date: d.slice(5), created: count }));

  return (
    <div className="px-6 py-6 max-w-[1500px]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Analytics</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Reports</h1>
        </div>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-56 h-9" data-testid="reports-project"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total issues</div><div className="font-display text-3xl">{issues.length}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Done</div><div className="font-display text-3xl text-emerald-500">{issues.filter(i=>i.status==="done").length}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total pts</div><div className="font-display text-3xl">{totalPts}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Done pts</div><div className="font-display text-3xl text-primary">{donePts}</div></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-display text-lg font-semibold mb-3">Status distribution</h3>
          <div className="h-56"><ResponsiveContainer><BarChart data={byStatus}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} /><YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} /><Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:6,fontSize:12}} /><Bar dataKey="count" fill="hsl(var(--primary))" radius={[4,4,0,0]} /></BarChart></ResponsiveContainer></div>
        </Card>
        <Card className="p-5">
          <h3 className="font-display text-lg font-semibold mb-3">Issue types</h3>
          <div className="h-56"><ResponsiveContainer><PieChart><Pie data={byType} innerRadius={40} outerRadius={80} dataKey="count" nameKey="name" paddingAngle={2}>{byType.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}</Pie><Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:6,fontSize:12}} /></PieChart></ResponsiveContainer></div>
        </Card>
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display text-lg font-semibold mb-3">Created — last 14 days</h3>
          <div className="h-64"><ResponsiveContainer><LineChart data={trend}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} /><YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} /><Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:6,fontSize:12}} /><Legend wrapperStyle={{fontSize:12}} /><Line type="monotone" dataKey="created" stroke="hsl(var(--primary))" strokeWidth={2} dot={{r:3}} /></LineChart></ResponsiveContainer></div>
        </Card>
      </div>
    </div>
  );
}
